/**
 * Payments API
 * RTK Query endpoints for payment management
 */

import { baseApi } from "./baseApi";

/**
 * Payment interface matching backend
 */
export interface Payment {
	id: string;
	organization_id?: string;
	organization_name?: string;
	lease_id: string;
	tenant_id: string;
	tenant_name?: string;
	amount: number;
	due_date: string;
	paid_date?: string;
	payment_method?:
		| "bank_transfer"
		| "cash"
		| "check"
		| "online"
		| "mobile_money"
		| "other";
	status: "pending" | "paid" | "overdue" | "cancelled" | "refunded";
	reference_number?: string;
	payment_proof_url?: string;
	payment_proof_uploaded_at?: string;
	verified_by_id?: string;
	verified_at?: string;
	verification_notes?: string;
	notes?: string;
	property_name?: string;
	property_id?: string;
	created_at: string;
	updated_at: string;
}

/**
 * Payment list response interface
 */
export interface PaymentListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Payment[];
}

/**
 * Create/Update payment request interface
 */
export interface PaymentData {
	organization: string;
	lease: string;
	tenant: string;
	amount: number;
	due_date: string;
	paid_date?: string;
	payment_method?:
		| "bank_transfer"
		| "cash"
		| "check"
		| "online"
		| "mobile_money"
		| "other";
	status?: "pending" | "paid" | "overdue" | "cancelled" | "refunded";
	reference_number?: string;
	notes?: string;
}

/**
 * Upload payment proof request
 */
export interface UploadPaymentProofData {
	payment_proof_url: string;
	reference_number?: string;
}

/**
 * Verify payment request (admin only)
 */
export interface VerifyPaymentData {
	verified: boolean;
	verification_notes?: string;
}

/**
 * Payments API endpoints
 */
export const paymentsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all payments (with filters, search, pagination)
		 */
		listPayments: builder.query<
			PaymentListResponse,
			{
				page?: number;
				page_size?: number;
				status?: string;
				lease?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.status) queryParams.append("status", params.status);
				if (params.lease) queryParams.append("lease", params.lease);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				const queryString = queryParams.toString();
				return {
					url: queryString ? `/payments/?${queryString}` : `/payments/`,
					method: "GET",
				};
			},
			transformResponse: (response: {
				count: number;
				next: string | null;
				previous: string | null;
				results: any[];
			}) => {
				// Use backend snake_case directly - no transformation needed
				return {
					...response,
					results:
						response.results?.map((payment: any) => {
							// Safely parse amount - handle Decimal types from backend
							const parseAmount = (val: any): number => {
								if (val === null || val === undefined) return 0;
								if (typeof val === "number") return isNaN(val) ? 0 : val;
								if (typeof val === "string") {
									const parsed = parseFloat(val);
									return isNaN(parsed) ? 0 : parsed;
								}
								return 0;
							};

							return {
								...payment,
								amount: parseAmount(payment.amount), // Ensure amount is always a number
								// Convert dates to ISO strings if needed
								due_date:
									typeof payment.due_date === "string"
										? payment.due_date
										: new Date(payment.due_date).toISOString(),
								paid_date: payment.paid_date
									? typeof payment.paid_date === "string"
										? payment.paid_date
										: new Date(payment.paid_date).toISOString()
									: undefined,
								payment_proof_uploaded_at: payment.payment_proof_uploaded_at
									? typeof payment.payment_proof_uploaded_at === "string"
										? payment.payment_proof_uploaded_at
										: new Date(payment.payment_proof_uploaded_at).toISOString()
									: undefined,
								verified_at: payment.verified_at
									? typeof payment.verified_at === "string"
										? payment.verified_at
										: new Date(payment.verified_at).toISOString()
									: undefined,
								created_at:
									typeof payment.created_at === "string"
										? payment.created_at
										: new Date(payment.created_at).toISOString(),
								updated_at:
									typeof payment.updated_at === "string"
										? payment.updated_at
										: new Date(payment.updated_at).toISOString(),
							};
						}) || [],
				};
			},
			providesTags: ["Payments"],
		}),

		/**
		 * Get payment by ID
		 */
		getPayment: builder.query<Payment, string>({
			query: (id) => ({
				url: `/payments/${id}`,
				method: "GET",
			}),
			transformResponse: (response: any) => {
				// Safely parse amount - handle Decimal types from backend
				const parseAmount = (val: any): number => {
					if (val === null || val === undefined) return 0;
					if (typeof val === "number") return isNaN(val) ? 0 : val;
					if (typeof val === "string") {
						const parsed = parseFloat(val);
						return isNaN(parsed) ? 0 : parsed;
					}
					return 0;
				};

				// Use backend snake_case directly - no transformation needed
				return {
					...response,
					amount: parseAmount(response.amount), // Ensure amount is always a number
					// Convert dates to ISO strings
					due_date:
						typeof response.due_date === "string"
							? response.due_date
							: new Date(response.due_date).toISOString(),
					paid_date: response.paid_date
						? typeof response.paid_date === "string"
							? response.paid_date
							: new Date(response.paid_date).toISOString()
						: undefined,
					payment_proof_uploaded_at: response.payment_proof_uploaded_at
						? typeof response.payment_proof_uploaded_at === "string"
							? response.payment_proof_uploaded_at
							: new Date(response.payment_proof_uploaded_at).toISOString()
						: undefined,
					verified_at: response.verified_at
						? typeof response.verified_at === "string"
							? response.verified_at
							: new Date(response.verified_at).toISOString()
						: undefined,
					created_at:
						typeof response.created_at === "string"
							? response.created_at
							: new Date(response.created_at).toISOString(),
					updated_at:
						typeof response.updated_at === "string"
							? response.updated_at
							: new Date(response.updated_at).toISOString(),
				};
			},
			providesTags: (result, error, id) => [{ type: "Payments", id }],
		}),

		/**
		 * Create new payment (admin only)
		 */
		createPayment: builder.mutation<Payment, PaymentData>({
			query: (data) => ({
				url: "/payments/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Payments"],
		}),

		/**
		 * Update payment (admin only)
		 */
		updatePayment: builder.mutation<
			Payment,
			{ id: string; data: Partial<PaymentData> }
		>({
			query: ({ id, data }) => ({
				url: `/payments/${id}`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Payments", id },
				"Payments",
			],
		}),

		/**
		 * Delete payment (admin only)
		 */
		deletePayment: builder.mutation<void, string>({
			query: (id) => ({
				url: `/payments/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Payments"],
		}),

		/**
		 * Upload payment proof (tenant)
		 */
		uploadPaymentProof: builder.mutation<
			Payment,
			{ id: string; data: UploadPaymentProofData }
		>({
			query: ({ id, data }) => {
				const params = new URLSearchParams();
				params.append("payment_proof_url", data.payment_proof_url);
				if (data.reference_number) {
					params.append("reference_number", data.reference_number);
				}
				return {
					url: `/payments/${id}/upload_proof?${params.toString()}`,
					method: "POST",
				};
			},
			invalidatesTags: (result, error, { id }) => [
				{ type: "Payments", id },
				"Payments",
			],
		}),

		/**
		 * Verify payment (admin only)
		 */
		verifyPayment: builder.mutation<
			Payment,
			{ id: string; data: VerifyPaymentData }
		>({
			query: ({ id, data }) => ({
				url: `/payments/${id}/verify`,
				method: "POST",
				body: {
					verification_notes: data.verification_notes || "",
				},
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Payments", id },
				"Payments",
			],
		}),

		/**
		 * Send payment reminders to tenants with overdue/pending payments
		 */
		sendPaymentReminders: builder.mutation<
			{
				success: boolean;
				message: string;
				result: {
					total_payments: number;
					emails_sent: number;
					notifications_created: number;
					errors: string[];
				};
			},
			void
		>({
			query: () => ({
				url: "/payments/send-reminders",
				method: "POST",
			}),
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListPaymentsQuery,
	useLazyListPaymentsQuery,
	useGetPaymentQuery,
	useCreatePaymentMutation,
	useUpdatePaymentMutation,
	useDeletePaymentMutation,
	useUploadPaymentProofMutation,
	useVerifyPaymentMutation,
	useSendPaymentRemindersMutation,
} = paymentsApi;
